<script>
import { DateTime } from 'luxon'

import { getCar } from '@/api/car'
import {
  cancelPickupVti, cancelReturnVti, getStockSlots, sendDispatchNotification
} from '@/api/cars'
import { getEmployees } from '@/api/dealer'
import ConnectOnboardingModal from '@/components/cars/ConnectOnboardingModal.vue'
import CreateLabelModal from '@/components/cars/CreateLabelModal.vue'
import CreateOfferModal from '@/components/cars/CreateOfferModal.vue'
import DatRequestModal from '@/components/cars/DatRequestModal.vue'
import CancelButton from '@/components/cars/edit/CancelButton.vue'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import Notices from '@/components/cars/edit/Notices.vue'
import Settings from '@/components/cars/edit/Settings.vue'
import SharedPageHeader from '@/components/cars/edit/SharedPageHeader.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import VtiPickupModal from '@/components/cars/edit/VtiPickupModal.vue'
import RequestTrashCarModal from '@/components/cars/RequestTrashCarModal.vue'
import SendOrderConfirmationModal from '@/components/cars/SendOrderConfirmationModal.vue'
import DatLogo from '@/components/ui/DatLogo.vue'
import QuickActionTiles from '@/components/ui/QuickActionTiles.vue'
import Form from '@/components/ui/widgets/Form.vue'
import FormSectionGrid from '@/components/ui/widgets/FormSectionGrid.vue'
import { nl2br } from '@/helpers/utils'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'

export default {
  name: 'MiaCarEditIndex',

  components: {
    'mia-cancel-button': CancelButton,
    'mia-car-edit-notices': Notices,
    'mia-car-edit-settings': Settings,
    'mia-car-edit-shared-page-header': SharedPageHeader,
    'mia-car-edit-vti-pickup-modal': VtiPickupModal,
    'mia-connect-onboarding-modal': ConnectOnboardingModal,
    'mia-create-label-modal': CreateLabelModal,
    'mia-create-offer-modal': CreateOfferModal,
    'mia-dat-request-modal': DatRequestModal,
    'mia-feature-select': FeatureSelect,
    'mia-form-section-grid': FormSectionGrid,
    'mia-form': Form,
    'mia-quick-action-tiles': QuickActionTiles,
    'mia-request-trash-car-modal': RequestTrashCarModal,
    'mia-send-order-confirmation-modal': SendOrderConfirmationModal,
    'mia-submit-button': SubmitButton
  },

  mixins: [HandlesBackendValidationErrors],

  data() {
    return {
      showVtiPickupModal: false,
      showVtiCancelReturnModal: false,
      showVtiCancelPickupModal: false,
      stockSlots: {},
      employees: [],
      cancelVtiPickupForm: {
        stockNumber: '0',
        assignee: ''
      },
      showRequestTrashCarModal: false,
      showSendOrderConfirmationModal: false,
      showConnectOnboardingModal: false,
      // Will be rendered in this order, re-arrange to sort
      tileGroups: [
        {
          id: 'selling',
          label: 'Verkaufen'
        },
        {
          id: 'movements',
          label: 'Fahrzeugbewegungen'
        },
        {
          id: 'documents',
          label: 'Dokumente & Formulare'
        },
        {
          id: 'finances',
          label: 'Finanzen'
        },
        {
          id: 'return',
          label: 'Rücknahme'
        },
        {
          id: 'data',
          label: 'Daten'
        }
      ],
      tiles: [
        {
          id: 'dat-request',
          label: 'DAT-Abfrage',
          group: 'data',
          icon: null,
          iconComponent: DatLogo,
          iconAttrs: {
            width: 82,
            height: 50.933
          },
          viewable: () => this.car.own_car && this.authDealer && this.authDealer.item_is_allowed_cars_advanced,
          listeners: {
            click: () => {
              this.requestDat()
            }
          }
        },
        {
          id: 'edit-configuration',
          label: 'Nachkonfigurieren',
          group: 'data',
          icon: 'tools-crescent-double',
          viewable: () => this.car.own_car,
          listeners: {
            click: () => {
              this.editConfiguration()
            }
          }
        },
        {
          id: 'import-configuration',
          label: 'Herstellerkonfiguration importieren',
          group: 'data',
          icon: 'download-dash-arrow',
          viewable: () => this.car.own_car,
          listeners: {
            click: () => {
              this.$router.push({ name: 'cars.edit.importConfiguration', params: { carId: this.car.id } })
            }
          }
        },
        {
          id: 'calculate-price',
          label: 'Preis berechnen',
          group: 'selling',
          icon: 'accounting-calculator',
          viewable: () => this.car.own_car && this.authUser.item_is_allowed_define_car_prices,
          disabled: () => (this.isCarSold ? 'Das Fahrzeug wurde verkauft' : false),
          listeners: {
            click: () => {
              this.calculatePrice()
            }
          }
        },
        {
          id: 'dispatch',
          label: 'Disponieren',
          group: 'selling',
          icon: 'shipment-deliver',
          viewable: () => this.car.own_car,
          disabled: () => (this.isCarSold ? 'Das Fahrzeug wurde verkauft' : false),
          listeners: {
            click: () => {
              this.dispatch()
            }
          }
        },
        {
          id: 'print-order-confirmation',
          label: 'Auftragsbestätigung drucken',
          group: 'documents',
          icon: 'bookmarks-document',
          viewable: true,
          disabled: () => (this.isOrderConfirmationPrintable ? false : 'Die Auftragsbestätigung kann nur für Fahrzeuge mit dem Status "Neubestellung Kunde", "Verkauft-Bestandsfahrzeug" oder "Ausgeliefert" gedruckt werden'),
          listeners: {
            click: () => {
              this.printOrderConfirmation()
            }
          }
        },
        {
          id: 'send-order-confirmation',
          label: 'Auftragsbestätigung versenden',
          group: 'documents',
          icon: 'mailing-service-email',
          viewable: () => this.authDealer.item_is_allowed_advanced_order_confirmation,
          disabled: () => (this.isOrderConfirmationPrintable ? false : 'Die Auftragsbestätigung kann nur für Fahrzeuge mit dem Status "Neubestellung Kunde", "Verkauft-Bestandsfahrzeug" oder "Ausgeliefert" gedruckt werden'),
          listeners: {
            click: () => {
              this.sendOrderConfirmation()
            }
          }
        },
        {
          id: 'launch-vap',
          label: 'Verkäuferarbeitsplatz starten',
          group: 'selling',
          icon: 'money-bag-euro',
          viewable: () => this.car.own_car,
          listeners: {
            click: () => {
              this.launchVap()
            }
          }
        },
        {
          id: 'create-offer',
          label: () => (this.authDealer.item_is_allowed_car_labeling ? 'Schnellangebot' : 'Schnellangebot / Preisauszeichnung'),
          group: 'selling',
          icon: 'human-resources-offer-man',
          viewable: () => this.authDealer.item_is_allowed_cars_advanced,
          listeners: {
            click: () => {
              this.createOffer()
            }
          }
        },
        {
          id: 'create-label',
          label: 'Preisauszeichnung',
          group: 'selling',
          icon: 'tag-euro',
          viewable: () => this.authDealer.item_is_allowed_car_labeling,
          listeners: {
            click: () => {
              this.createLabel()
            }
          }
        },
        {
          id: 'open-docuware-link',
          label: 'DocuWare Link öffnen',
          group: 'documents',
          icon: 'share-external',
          viewable: () => this.isAllowedDealer('docuware'),
          disabled: () => (this.car.item_docuware_link === ''),
          listeners: {
            click: () => {
              this.openDocuWareLink()
            }
          }
        },
        {
          id: 'create-wltp-label',
          label: 'DocuWare Link öffnen',
          group: 'documents',
          icon: 'share-external',
          viewable: () => this.isAllowedDealer('docuware'),
          disabled: () => (this.car.item_docuware_link === ''),
          listeners: {
            click: () => {
              this.openDocuWareLink()
            }
          }
        },
        {
          id: 'create-wltp-label',
          label: 'WLTP Datenblatt erzeugen',
          group: 'documents',
          icon: 'earth-1',
          viewable: true,
          listeners: {
            click: () => {
              this.createWLTPLabel()
            }
          }
        },
        {
          id: 'create-privacy-doc',
          label: 'Kunden Datenschutzerklärung erzeugen',
          group: 'documents',
          icon: 'check-shield',
          viewable: true,
          listeners: {
            click: () => {
              this.createPrivacyDoc()
            }
          }
        },
        {
          id: 'create-handover-doc',
          label: 'Übergabeformular erzeugen',
          group: 'documents',
          icon: 'email-action-read-document',
          viewable: true,
          listeners: {
            click: () => {
              this.createHandoverDoc()
            }
          }
        },
        {
          id: 'sell-car',
          label: 'Verkaufsmeldung',
          group: 'movements',
          icon: 'accounting-document',
          viewable: () => (this.car.item_access_locked === 'N' || this.isAllowed('handle_car_customer_access')),
          disabled: () => (this.isCarSellable ? false : 'Die Verkaufsmeldung kann nur für Fahrzeuge erstellt werden, die sich NICHT im Status "Neubestellung Kunde", "Verkauft-Bestandsfahrzeug" oder "Ausgeliefert" gedruckt werden'),
          listeners: {
            click: () => {
              this.sellCar()
            }
          }
        },
        {
          id: 'offer-car',
          label: 'Verkaufen',
          group: 'selling',
          icon: 'check-payment-sign',
          viewable: () => this.authDealer.item_is_allowed_vap,
          listeners: {
            click: () => {
              this.offerCar()
            }
          }
        },
        {
          id: 'register-car',
          label: 'Zulassungsmeldung erstellen',
          group: 'movements',
          icon: 'business-contract-approve',
          viewable: () => this.car.own_car,
          listeners: {
            click: () => {
              this.registerCar()
            }
          }
        },
        {
          id: 'disposition-notification',
          label: () => this.dispatchNotificationLabel,
          group: 'movements',
          icon: 'mailing-service-email',
          viewable: () => this.car.own_car && this.authDealer.item_is_allowed_customer_portal,
          disabled: () => !this.allowDispatchNotification,
          listeners: {
            click: () => {
              this.sendCarDispatchNotification()
            }
          }
        },
        {
          id: 'plan-car-disposition',
          label: 'Auslieferung planen',
          group: 'movements',
          icon: 'notes-book',
          viewable: () => this.car.own_car,
          listeners: {
            click: () => {
              this.$router.push(`/cars/${this.car.id}/disponieren#auslieferung`)
            }
          }
        },
        {
          id: 'add-car',
          label: 'Inzahlungnahme anlegen',
          group: 'return',
          icon: 'transportation-ticket-car-transfer-1',
          viewable: () => this.car.own_car,
          disabled: () => (this.isCarDispatched ? 'Das Fahrzeug befindet sich im Status "Ausgeliefert"' : false),
          listeners: {
            click: () => {
              this.addCar()
            }
          }
        },
        {
          id: 'reassign',
          label: 'Zuständigkeit übergeben',
          icon: 'human-resources-employee-previous',
          viewable: () => this.car.own_car,
          listeners: {
            click: () => {
              this.reassign()
            }
          }
        },
        {
          id: 'add-costs',
          label: 'Kosten buchen',
          group: 'finances',
          icon: 'accounting-coins-bill',
          viewable: () => this.car.own_car,
          listeners: {
            click: () => {
              this.addCosts()
            }
          }
        },
        {
          id: 'add-financial',
          label: 'Finanzierungsertrag buchen',
          group: 'finances',
          icon: 'accounting-invoice',
          viewable: () => this.car.own_car,
          listeners: {
            click: () => {
              this.addFinancial()
            }
          }
        },
        {
          id: 'add-accessories-sold',
          label: 'Zubehörverkauf buchen',
          group: 'finances',
          icon: 'module-three-2',
          viewable: () => this.car.own_car,
          listeners: {
            click: () => {
              this.addAccessoriesSold()
            }
          }
        },
        {
          id: 'add-pool-payout',
          label: 'Subvention buchen',
          group: 'finances',
          icon: 'cash-network',
          viewable: () => this.car.own_car,
          listeners: {
            click: () => {
              this.addPoolPayout()
            }
          }
        },
        {
          id: 'prebook-provisions',
          label: 'Prämien vorbuchen',
          group: 'finances',
          icon: 'app-window-badge',
          viewable: () => this.car.own_car && this.authUser.item_is_allowed_define_car_prices,
          disabled: () => (this.isCarDispatched ? 'Das Fahrzeug befindet sich im Status "Ausgeliefert"' : false),
          listeners: {
            click: () => {
              this.prebookProvisions()
            }
          }
        },
        {
          id: 'dispatch-car',
          label: 'Auslieferungsmeldung erstellen',
          group: 'movements',
          icon: 'car-tool-keys',
          viewable: () => this.car.own_car,
          disabled: () => (this.isCarDispatchable ? false : 'Die Auslieferungsmeldung kann nur für Fahrzeuge mit dem Status "Neubestellung Kunde", "Auf Lager", "Verkauft-Bestandsfahrzeug" oder "Kundenbestellung auf Lager" erstellt werden'),
          listeners: {
            click: () => {
              this.dispatchCar()
            }
          }
        },
        {
          id: 'block-car',
          label: 'Reservieren',
          group: 'selling',
          icon: 'parking-p-1',
          viewable: true,
          disabled: () => (this.isCarBlocked ? 'Das Fahrzeug ist bereits verkauft bzw. ausgeliefert' : false),
          listeners: {
            click: () => {
              this.blockCar()
            }
          }
        },
        {
          id: 'get-revenue',
          label: 'Ertragsrechnung',
          group: 'finances',
          icon: 'currency-euro-circle',
          viewable: () => (this.car.item_access_locked === 'N' || this.isAllowed('handle_car_customer_access')) && this.car.own_car && (this.authUser.item_is_allowed_show_followingup || parseInt(this.car.item_sold_seller, 10) === parseInt(this.authUser.id, 10)),
          listeners: {
            click: () => {
              this.getRevenue()
            }
          }
        },
        {
          id: 'start-vti-return',
          label: 'VTI Rückgabe',
          group: 'return',
          icon: 'navigation-car-pin',
          viewable: () => this.car.own_car,
          disabled: () => {
            if (parseInt(this.car.item_status, 10) !== 5) {
              return 'Das Fahrzeug muss sich im Status "Ausgeliefert" befinden'
            }

            if (this.car.item_vti_status === 'pending') {
              return 'Die VTI-Rückgabe wurde bereits gestartet'
            }

            if (this.car.item_vti_status === 'completed') {
              return 'Die VTI-Rückgabe wurde bereits abgewickelt'
            }

            return false
          },
          listeners: {
            click: () => {
              this.startVtiReturn()
            }
          }
        },
        {
          id: 'start-vti-pickup',
          label: 'VTI Abholung',
          group: 'return',
          icon: 'truck-empty',
          viewable: () => this.car.own_car,
          disabled: () => {
            if (parseInt(this.car.item_status, 10) !== 5) {
              return 'Das Fahrzeug muss sich im Status "Ausgeliefert" befinden'
            }

            if (this.car.item_vti_status !== 'pending') {
              return 'Die VTI-Rückgabe wurde noch nicht gestartet'
            }

            return false
          },
          listeners: {
            click: () => {
              this.startVtiPickup()
            }
          }
        },
        {
          id: 'cancel-vti-return',
          label: 'VTI-Rückgabe abbrechen',
          group: 'return',
          icon: 'navigation-car-pin',
          viewable: () => this.car.own_car && this.car.item_vti_status === 'pending',
          disabled: () => {
            if (parseInt(this.car.item_status, 10) !== 5) {
              return 'Das Fahrzeug muss sich im Status "Ausgeliefert" befinden'
            }

            return false
          },
          listeners: {
            click: () => {
              this.cancelVtiReturn()
            }
          }
        },
        {
          id: 'cancel-vti-pickup',
          label: 'VTI-Abholung zurücksetzen',
          group: 'return',
          icon: 'navigation-car-pin',
          viewable: () => this.car.own_car && this.car.item_vti_status === 'completed',
          disabled: () => {
            if (parseInt(this.car.item_status, 10) !== 5) {
              return 'Das Fahrzeug muss sich im Status "Ausgeliefert" befinden'
            }

            return false
          },
          listeners: {
            click: () => {
              this.cancelVtiPickup()
            }
          }
        },
        {
          id: 'close-file',
          label: 'Fahrzeugakte schließen',
          icon: 'lock-2',
          viewable: () => this.car.own_car && this.authUser.item_is_allowed_open_close_cars && this.car.item_closed === 'N',
          disabled: () => {
            if (!this.hasAkDate) {
              return 'Die Fahrzeugakte kann erst geschlossen werden, wenn das Datum AK Meldung beim Hersteller eingetragen wurde.'
            }

            return false
          },
          listeners: {
            click: () => {
              this.closeFile()
            }
          }
        },
        {
          id: 'open-file',
          label: 'Fahrzeugakte wiedereröffnen',
          icon: 'lock-unlock-1',
          viewable: () => this.car.own_car && this.authUser.item_is_allowed_open_close_cars && this.car.item_closed === 'Y',
          listeners: {
            click: () => {
              this.openFile()
            }
          }
        },
        {
          id: 'trash-car',
          label: 'Fahrzeug löschen',
          classes: 'trash-car-action',
          iconComponent: 'img',
          iconAttrs: {
            src: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAMAAABg3Am1AAAAq1BMVEUAAABNTU0AAAAjIyMAAAAjIyMNDQ0sLCwSEhIAAAAAAAAAAAALCwsHBwcAAAAAAAAAAAAAAAAAAAAAAAAKCgoAAAAAAAAAAAAAAAAAAAACAgIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEBAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAJCQkAAAAAAAAAAAAAAAADAwMEBAQGBgYICAhSeNEmAAAANHRSTlMAChAdICQmKSowP0BGS0xPUF9gY2Vub3B/gIGPkJKcn6Clpq+wuL/AwsfP0NPX3+Dg6e/w2dOBTgAAAmxJREFUeNqVlOF20zAMhUUwQFtYXQNNDB3NIKyQsITFl8L7Pxmx5aoebne678dOKutakiyNzrBe0ZMogZKeQDECY0GXYzFh6VFWSdIKAZWcrk8k3YukBnY7oBb3HjC5QCQLAIvwR9zzHihAJLdAHcLcirvkd6TDQaL9MV+hD+7osqI3QG0GMJZbxQymBjaZYAb0RCxxRXgMx+5EIzCjDMdWM0iBpXfnuxylLZ7HXsanMqW0zki25JmvV7Gf7XY910BLJ2kBPV9vW0wYMhCkd3nHBUM0pD/PTK7QxXe1Zd09Lujq0h7evonJL7jGHFMGv/5Qu3IXbYvl9+HPC7ZFjcm1g4zyrAqsgl7J58TXdJ4Oo6xaRDTvaWCduDBSd/ugv1bUUrGg3OENN9YOwOBt/GvHQ3msWOou/S1l3IO4pz6xATAn/u2UIcDA1e3SpA2HKCkjbLo43krSIUQOBxDHuKfHEOcCmOjY83acC6GW1ZYD9Ly+XlcksfuqWipxn8X+G75tnLFgS0kITxsXuwIzBLFjRZ0qDCIVxfJcY61VdF4xHTcOgOaJaSTdTJFQNGGqLU/hJQrqAEu7ECdXECsMJWg/BCNQnNhi53P4X1D4iwDQQ+TVNty5BAAiKJbVcgr15rnMsgpTxfZcoEcAo756Rc+ox++PRK+/7/c/6f249/ZE4MLszMB8oKsXBnv38i0t/gLvZvgDD7+x8rU1YVDugGHTAb9ubqqRK7gHfoj9rvL0QCN3Qx0/74noc26PobTLDq7PCVwoptDWTqHbT/0U2g/Nl2C9/mZTu0cXx8eViMKj9piY05faOTFdXGD/B2OZpzbg5gHSAAAAAElFTkSuQmCC'
          },
          viewable: () => this.car.own_car && this.canTrashCar,
          listeners: {
            click: () => {
              this.trashCar()
            }
          }
        },
        {
          id: 'trash-car-request',
          label: 'Löschanfrage',
          classes: 'trash-car-action',
          iconComponent: 'img',
          iconAttrs: {
            src: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAMAAABg3Am1AAAAq1BMVEUAAABNTU0AAAAjIyMAAAAjIyMNDQ0sLCwSEhIAAAAAAAAAAAALCwsHBwcAAAAAAAAAAAAAAAAAAAAAAAAKCgoAAAAAAAAAAAAAAAAAAAACAgIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEBAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAJCQkAAAAAAAAAAAAAAAADAwMEBAQGBgYICAhSeNEmAAAANHRSTlMAChAdICQmKSowP0BGS0xPUF9gY2Vub3B/gIGPkJKcn6Clpq+wuL/AwsfP0NPX3+Dg6e/w2dOBTgAAAmxJREFUeNqVlOF20zAMhUUwQFtYXQNNDB3NIKyQsITFl8L7Pxmx5aoebne678dOKutakiyNzrBe0ZMogZKeQDECY0GXYzFh6VFWSdIKAZWcrk8k3YukBnY7oBb3HjC5QCQLAIvwR9zzHihAJLdAHcLcirvkd6TDQaL9MV+hD+7osqI3QG0GMJZbxQymBjaZYAb0RCxxRXgMx+5EIzCjDMdWM0iBpXfnuxylLZ7HXsanMqW0zki25JmvV7Gf7XY910BLJ2kBPV9vW0wYMhCkd3nHBUM0pD/PTK7QxXe1Zd09Lujq0h7evonJL7jGHFMGv/5Qu3IXbYvl9+HPC7ZFjcm1g4zyrAqsgl7J58TXdJ4Oo6xaRDTvaWCduDBSd/ugv1bUUrGg3OENN9YOwOBt/GvHQ3msWOou/S1l3IO4pz6xATAn/u2UIcDA1e3SpA2HKCkjbLo43krSIUQOBxDHuKfHEOcCmOjY83acC6GW1ZYD9Ly+XlcksfuqWipxn8X+G75tnLFgS0kITxsXuwIzBLFjRZ0qDCIVxfJcY61VdF4xHTcOgOaJaSTdTJFQNGGqLU/hJQrqAEu7ECdXECsMJWg/BCNQnNhi53P4X1D4iwDQQ+TVNty5BAAiKJbVcgr15rnMsgpTxfZcoEcAo756Rc+ox++PRK+/7/c/6f249/ZE4MLszMB8oKsXBnv38i0t/gLvZvgDD7+x8rU1YVDugGHTAb9ubqqRK7gHfoj9rvL0QCN3Qx0/74noc26PobTLDq7PCVwoptDWTqHbT/0U2g/Nl2C9/mZTu0cXx8eViMKj9piY05faOTFdXGD/B2OZpzbg5gHSAAAAAElFTkSuQmCC'
          },
          viewable: () => this.car.own_car,
          listeners: {
            click: () => {
              this.showRequestTrashCarModal = true
            }
          }
        },
        {
          id: 'generate-connect-onboarding-document',
          label: 'Connect Onboarding',
          group: 'documents',
          icon: 'flash-1',
          viewable: () => this.car.own_car && this.authDealer && this.authDealer.item_is_allowed_connect_onboarding && ['CUPRA', 'SEAT', 'SKODA'].includes(this.car.item_mobile_make),
          listeners: {
            click: () => {
              this.showConnectOnboardingModal = true
            }
          }
        }
      ],
      showDatRequestModal: false,
      showCreateOfferModal: false,
      showCreateLabelModal: false
    }
  },

  computed: {
    car: {
      get() {
        return this.$store.state.car.car
      },

      set(car) {
        this.$store.commit('setCar', car)
      }
    },

    dispatchNotificationLabel() {
      return `Bereitstellungsanzeige versenden<br/><small>${this.car.item_dispatch_notification_sent_at ? DateTime.fromSQL(this.car.item_dispatch_notification_sent_at).toFormat('dd.MM.yyyy, HH:mm') : ''}</small>`
    },

    allowDispatchNotification() {
      return this.car.item_dispatch_notification_sent_at === null && !!this.car.all_dispatches && this.car.all_dispatches.length > 0
    },

    isCarBlocked() {
      return [4, 5].includes(parseInt(this.car.item_status, 10))
    },

    isCarDispatchable() {
      const status = parseInt(this.car.item_status, 10)

      return [2, 3, 4, 7].includes(status)
    },

    isCarDispatched() {
      return parseInt(this.car.item_status, 10) === 5
    },

    isCarClosable() {
      return this.car.own_car && this.authUser.item_is_allowed_open_close_cars && this.car.item_closed === 'N'
    },

    isCarOpenable() {
      return this.car.own_car && this.authUser.item_is_allowed_open_close_cars && this.car.item_closed === 'Y'
    },

    isCarSold() {
      return [4, 5].includes(parseInt(this.car.item_status, 10))
    },

    isOrderConfirmationPrintable() {
      return [2, 4, 5].includes(parseInt(this.car.item_status, 10))
    },

    isCarSellable() {
      return ![2, 4, 5].includes(parseInt(this.car.item_status, 10))
    },

    hasAkDate() {
      if (![1, 2].includes(parseInt(this.authDealer.id, 10))) {
        return true
      }

      if (['Gebrauchtwagen', 'Jahreswagen'].includes(this.car.item_type)) {
        return true
      }

      return this.car.item_ak_date !== '' && this.car.item_ak_date !== null && this.car.item_ak_date !== undefined && this.car.item_ak_date !== '0000-00-00'
    },

    canSubmitVtiCancelForm() {
      return this.cancelVtiPickupForm.stockNumber !== '0' && this.cancelVtiPickupForm.assignee !== ''
    },

    canTrashCar() {
      const dealerOwnsCar = parseInt(this.car.item_dealer, 10) === parseInt(this.authDealer.id, 10)
      const isRootOrAdmin = ['root', 'admin'].includes(this.authUser.item_group)
      const hasPermission = this.authUser.item_is_allowed_trash_cars

      return dealerOwnsCar && (isRootOrAdmin || hasPermission)
    }
  },

  async created() {
    this.stockSlots = await getStockSlots(this.car.id)
    this.employees = await getEmployees()
  },

  methods: {
    nl2br,

    createOffer() {
      this.showCreateOfferModal = true
    },

    createLabel() {
      this.showCreateLabelModal = true
    },

    createWLTPLabel() {
      window.open(`${window.baseUrl}/environment/wltpLabel/${this.car.id}`, '_blank').focus()
    },

    openDocuWareLink() {
      window.open(`${this.car.item_docuware_link}`, '_blank').focus()
    },

    createPrivacyDoc() {
      window.open(`${window.baseUrl}/customerDocuments/privacy/${this.car.id}`, '_blank').focus()
    },

    createHandoverDoc() {
      window.open(`${window.baseUrl}/customerDocuments/handover/${this.car.id}`, '_blank').focus()
    },

    closeFile() {
      if (!this.hasAkDate) {
        return
      }

      mia.car.openclose(this.car.id, 'close')
    },

    openFile() {
      mia.car.openclose(this.car.id, 'open')
    },

    launchVap() {
      this.$router.push(`/offer/sell/${this.car.id}`)
    },

    dispatch() {
      this.$router.push(`/legacy/car_accept/${this.car.id}`)
    },

    requestDat() {
      this.showDatRequestModal = true
    },

    editConfiguration() {
      this.$router.push(`/configurator/${this.car.id}`)
    },

    calculatePrice() {
      this.$router.push(`/legacy/car_calculate/${this.car.id}`)
    },

    printOrderConfirmation() {
      mia.car.confirmation.create(this.car.id)
    },

    sendOrderConfirmation() {
      this.showSendOrderConfirmationModal = true
    },

    sellCar() {
      this.$router.push(`/legacy/car_sell/${this.car.id}`)
    },

    offerCar() {
      this.$router.push(`/offer/sell/${this.car.id}`)
    },

    impartCar() {
      this.$router.push(`/offer/impart/${this.car.id}`)
    },

    registerCar() {
      this.$router.push(`/legacy/car_register/${this.car.id}`)
    },

    dispatchCar() {
      this.$router.push(`/legacy/car_dispatch/${this.car.id}`)
    },

    blockCar() {
      this.$router.push(`/legacy/car_block/${this.car.id}`)
    },

    addCar() {
      this.$router.push(`/legacy/car_add/${this.car.id}`)
    },

    reassign() {
      this.$router.push(`/legacy/car_reassign/${this.car.id}`)
    },

    addCosts() {
      this.$router.push(`/legacy/car_costs_add/${this.car.id}`)
    },

    addFinancial() {
      this.$router.push(`/legacy/car_financial_add/${this.car.id}`)
    },

    addAccessoriesSold() {
      this.$router.push(`/legacy/accessories_sold_add/${this.car.id}`)
    },

    addPoolPayout() {
      this.$router.push(`/legacy/car_pool_payout_add/${this.car.id}`)
    },

    prebookProvisions() {
      this.$router.push(`/legacy/car_prebook_provisions/${this.car.id}`)
    },

    getRevenue() {
      this.$router.push(`/legacy/car_revenue/${this.car.id}`)
    },

    startVtiReturn() {
      this.$router.push(`/cars/${this.car.id}/vti-rueckgabe`)
    },

    startVtiPickup() {
      this.showVtiPickupModal = true
    },

    async onVtiFinished() {
      this.car = await getCar({
        carId: this.car.id
      })
      this.showVtiPickupModal = false
    },

    cancelVtiReturn() {
      this.showVtiCancelReturnModal = true
    },

    async onCancelVtiReturnConfirm() {
      const [error] = await cancelReturnVti(this.car.id)

      if (error) {
        console.error(error)
        window.eventBus.$emit('mia-error', {
          error: ['Der VTI-Vorgang konnte nicht abgebrochen werden']
        })
        return
      }

      this.car = await getCar({
        carId: this.car.id
      })
      this.showVtiCancelReturnModal = false

      window.eventBus.$emit('mia-info', {
        info: ['Der VTI-Vorgang wurde erfolgreich abgebrochen']
      })
    },

    cancelVtiPickup() {
      this.showVtiCancelPickupModal = true
    },

    async onCancelVtiPickupConfirm() {
      this.resetErrors()
      this.$wait.start('canceling vti pickup')

      const [error] = await cancelPickupVti(this.car.id, {
        item_stock_number: this.cancelVtiPickupForm.stockNumber,
        item_assignee: this.cancelVtiPickupForm.assignee
      })

      this.$wait.end('canceling vti pickup')

      if (!error) {
        this.car = await getCar({
          carId: this.car.id
        })

        window.eventBus.$emit('mia-info', {
          info: ['VTI-Abholung erfolgreich zurückgesetzt.']
        })

        this.showVtiCancelPickupModal = false
        this.cancelVtiPickupForm.stockNumber = '0'
        this.cancelVtiPickupForm.assignee = ''
      } else {
        this.handleBackendValidationErrors(error)

        window.eventBus.$emit('mia-error', {
          error: ['Fehler beim Speichern der Daten.']
        })
      }
    },

    trashCar() {
      mia.car.remove.confirm(this.car.id, this.car.item_fin, () => {
        window.location.href = '/'
      })
    },

    async sendCarDispatchNotification() {
      const [error] = await sendDispatchNotification(this.car.id)

      if (error) {
        console.error(error)
        window.eventBus.$emit('mia-error', {
          error: ['Das versenden der Bereitstellungsanzeige ist fehlgeschlagen.']
        })
        return
      }

      this.car = await getCar({
        carId: this.car.id
      })

      window.eventBus.$emit('mia-info', {
        info: ['Die Bereitstellungsanzeige wurde erfolgreich versendet.']
      })
    },

    async onDatRequestUpdateCar() {
      this.showDatRequestModal = false
      this.car = await getCar({
        carId: this.car.id
      })
    }
  }
}
</script>

<template>
  <div>
    <mia-car-edit-shared-page-header />

    <mia-owns-car>
      <div class="max-w-5xl px-16 mx-auto mt-8">
        <mia-car-edit-settings />
        <hr>
        <mia-car-edit-notices class="mt-6" />
      </div>
    </mia-owns-car>
    <div
      v-if="!car.own_car"
      class="max-w-5xl px-16 mx-auto mt-8"
    >
      <div class="mb-1 mr-2 tracking-wider text-gray-800 font-semibold">
        Freigegebene Anmerkungen für angeschlossene Händler
      </div>
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="car.item_notes_shared"
        v-html="nl2br(car.item_notes_shared)"
      />
      <!-- eslint-enable vue/no-v-html -->
      <div v-else>
        -
      </div>
    </div>

    <mia-quick-action-tiles
      class="mt-16 mx-auto"
      :groups="tileGroups"
      :tiles="tiles"
    />

    <mia-request-trash-car-modal
      v-if="showRequestTrashCarModal"
      :show="showRequestTrashCarModal"
      :car-id="car.id"
      @closed="showRequestTrashCarModal = false"
    />

    <mia-has-feature feature="advanced_order_confirmation">
      <mia-send-order-confirmation-modal
        v-if="showSendOrderConfirmationModal"
        :car="car"
        :show="showSendOrderConfirmationModal"
        @cancel="showSendOrderConfirmationModal = false"
        @closed="showSendOrderConfirmationModal = false"
      />
    </mia-has-feature>

    <mia-owns-car>
      <mia-has-feature feature="connect_onboarding">
        <mia-connect-onboarding-modal
          v-if="showConnectOnboardingModal"
          :car="car"
          :show="showConnectOnboardingModal"
          @cancel="showConnectOnboardingModal = false"
          @closed="showConnectOnboardingModal = false"
        />
      </mia-has-feature>

      <template v-if="parseInt(car.item_status, 10) === 5">
        <mia-car-edit-vti-pickup-modal
          v-if="car.item_vti_status === 'pending' && showVtiPickupModal"
          :car-id="car.id"
          @closed="showVtiPickupModal = false"
          @vti-finished="onVtiFinished"
        />

        <mia-confirmation-modal
          v-if="showVtiCancelReturnModal"
          id="cancel-vti-return-confirmation-modal"
          title="VTI-Rückgabe abbrechen"
          confirm-classes="border-red-500 bg-red-500 text-white hover:bg-red-600 hover:border-red-600"
          confirm-text="Rückgabe abbrechen"
          cancel-text="Schließen"
          width="40%"
          @closed="showVtiCancelReturnModal = false"
          @cancel="showVtiCancelReturnModal = false"
          @confirm="onCancelVtiReturnConfirm"
        >
          <div class="p-4">
            Sind Sie sicher, dass der VTI-Vorgang abgebrochen werden soll? Der Lagerplatz und die Zuständigkeit werden dabei zurückgesetzt.
          </div>
        </mia-confirmation-modal>

        <mia-modal
          v-if="showVtiCancelPickupModal"
          id="cancel-vti-pickup-modal"
          classes="v--modal flex flex-col"
          width="60%"
          height="auto"
          :show="true"
          @closed="showVtiCancelPickupModal = false"
        >
          <mia-modal-header>
            <h3 class="py-0 my-0">
              VTI-Abholung zurücksetzen
            </h3>
          </mia-modal-header>

          <mia-form @submit="onCancelVtiPickupConfirm">
            <div class="p-4">
              <mia-form-section-grid>
                <!-- Stock Number -->
                <mia-feature-select
                  v-model="cancelVtiPickupForm.stockNumber"
                  label="Lagerplatz"
                  :errors="getErrors('item_stock_number')"
                  required
                  @input="clearErrors('item_stock_number')"
                >
                  <template #options>
                    <option
                      value="0"
                      disabled
                    >
                      Ohne Lagerplatz
                    </option>
                    <optgroup
                      v-for="(slots, location) in stockSlots"
                      :key="location"
                      :label="location"
                    >
                      <option
                        v-for="slot in slots"
                        :key="slot.id"
                        :value="slot.id"
                        v-text="slot.title"
                      />
                    </optgroup>
                  </template>
                </mia-feature-select>

                <!-- Assignee -->
                <mia-feature-select
                  v-model="cancelVtiPickupForm.assignee"
                  label="Zuständig"
                  :errors="getErrors('item_assignee')"
                  required
                  @input="clearErrors('item_assignee')"
                >
                  <template #options>
                    <option value="">
                      Zuständigkeit auswählen
                    </option>
                    <option
                      v-for="employee in employees"
                      :key="employee.id"
                      :value="employee.id"
                      :selected="employee.id === cancelVtiPickupForm.assignee"
                    >
                      {{ employee.full_name }}
                    </option>
                  </template>
                </mia-feature-select>
              </mia-form-section-grid>
            </div>

            <template #actions>
              <mia-cancel-button
                label="Schließen"
                type="button"
                @click="showVtiCancelPickupModal = false"
              />
              <mia-submit-button
                label="VTI-Abholung zurücksetzen"
                type="button"
                class="ml-3"
                :disabled="$wait.any || !canSubmitVtiCancelForm"
                :is-loading="$wait.is('canceling vti pickup')"
                @click="onCancelVtiPickupConfirm"
              />
            </template>
          </mia-form>
        </mia-modal>
      </template>

      <mia-has-feature feature="cars_advanced">
        <mia-dat-request-modal
          v-if="showDatRequestModal"
          :show="showDatRequestModal"
          :car="car"
          @cancel="showDatRequestModal = false"
          @closed="showDatRequestModal = false"
          @done="onDatRequestUpdateCar"
        />
        <mia-create-offer-modal
          v-if="showCreateOfferModal"
          :show="showCreateOfferModal"
          :car="car"
          @cancel="showCreateOfferModal = false"
          @closed="showCreateOfferModal = false"
          @done="showCreateOfferModal = false"
        />

        <mia-has-feature feature="car_labeling">
          <mia-create-label-modal
            v-if="showCreateLabelModal"
            :show="showCreateLabelModal"
            :car="car"
            @cancel="showCreateLabelModal = false"
            @closed="showCreateLabelModal = false"
            @done="showCreateLabelModal = false"
          />
        </mia-has-feature>
      </mia-has-feature>
    </mia-owns-car>
  </div>
</template>

<style>
.trash-car-action img {
  filter: opacity(0.7);
  width: auto;
  height: 100%;
}

.trash-car-action:hover {
  @apply bg-red-500;
}

.trash-car-action:hover img {
  filter: invert(1);
}
</style>
